import { PRODUCT_TYPE } from '../constants/productType'
import { Theme } from '../constants/theme/theme'

export interface ProductTypeProviderValuesInput {
  isWellbeingEnabled: boolean
  productType: PRODUCT_TYPE
  isUserLoggedIn?: boolean
  lastLoggedInUserProductType?: PRODUCT_TYPE
}

export interface ProductTypeProviderValues {
  isLyraSelect: boolean
  isWellbeing: boolean
  productType: PRODUCT_TYPE
}

/**
 * Derives the product theme context from the backend product type and the Wellbeing rollout flag.
 *
 * WFMH is the default product experience. Aurora is the theme for WFMH and Wellbeing, while
 * Lyra Select owns its product experience and theme decisions.
 * Lyra Select has its own Select-specific theme, microsite, and registration experience;
 * Wellbeing follows the same Aurora experience as WFMH.
 *
 * Logged-out flows use the last logged-in product type for product context, so the product (and
 * therefore the theme) stays stable across the logout transition. Wellbeing is exposed only when
 * its rollout flag is enabled.
 */
export const getProductTypeProviderValues = ({
  isWellbeingEnabled,
  isUserLoggedIn = true,
  productType,
  lastLoggedInUserProductType,
}: ProductTypeProviderValuesInput): ProductTypeProviderValues => {
  const isLyraSelect = productType === PRODUCT_TYPE.SELECT
  const isWellbeing = isWellbeingEnabled && productType === PRODUCT_TYPE.WELLBEING
  const activeProductType = isUserLoggedIn ? productType : lastLoggedInUserProductType
  const shouldExposeProductContext = activeProductType !== PRODUCT_TYPE.WELLBEING || isWellbeingEnabled

  return {
    isLyraSelect,
    isWellbeing,
    productType: shouldExposeProductContext && activeProductType ? activeProductType : PRODUCT_TYPE.WFMH,
  }
}

export interface EssentialsThemeInput {
  productType: PRODUCT_TYPE
  themeType: Theme
  colorScheme?: 'light' | 'dark' | null
}

/**
 * Derives the theme Essentials (Library) renders, which the host sends as the `x-lyra-theme`
 * header/param. Lyra Select keeps its own `wfmh` theme; every other product follows the member's
 * app-wide light/dark selection, resolving `system` against the OS color scheme.
 */
export const getEssentialsTheme = ({ productType, themeType, colorScheme }: EssentialsThemeInput): string => {
  if (productType === PRODUCT_TYPE.SELECT) {
    return 'wfmh'
  }
  const isDark = themeType === Theme.DARK || (themeType === Theme.SYSTEM && colorScheme === 'dark')
  return isDark ? 'aurora_dark' : 'aurora_light'
}
